/**
 * ============================================================================
 * Warehouse 模块 API 工厂函数
 * ============================================================================
 * 
 * 📋 文件职责：
 * - 定义 Warehouse 模块的所有 API 请求方法
 * - 通过工厂模式实现请求客户端的依赖注入
 * - 提供完整的 TypeScript 类型安全
 * 
 * 🎯 核心概念：工厂模式
 * - 工厂函数：createWarehouseApi(client) - 接收请求客户端，返回 API 方法集合
 * - 依赖注入：不依赖具体的请求实现（axios、uni.request 等）
 * - 平台无关：Web 端和 App 端都可以使用，只需提供不同的适配器
 * 
 * 💡 设计理念：
 * - "给我一个能发送请求的工具，我就能为你生成所有 API 方法"
 * - 请求客户端是"工具"，API 方法是"产品"，工厂函数是"生产线"
 * 
 * 📖 使用方式：
 * 
 * **Web 端（Vben Admin + axios）**：
 * ```typescript
 * // 1. 创建适配器（让 Vben 的 requestClient 符合 IRequestClient 接口）
 * import { requestClient } from '#/api/request'
 * import type { IRequestClient } from '@bayes-mes/shared/api/core/client'
 * 
 * const webAdapter: IRequestClient = {
 *   get: (url, config) => requestClient.get(url, config).then(res => res.data),
 *   post: (url, data, config) => requestClient.post(url, data, config).then(res => res.data),
 *   put: (url, data, config) => requestClient.put(url, data, config).then(res => res.data),
 *   delete: (url, config) => requestClient.delete(url, config).then(res => res.data),
 * }
 * 
 * // 2. 调用工厂函数，传入适配器
 * import { createWarehouseApi } from '@bayes-mes/shared/api/warehouse/factory'
 * export const warehouseApi = createWarehouseApi(webAdapter)
 * 
 * // 3. 使用生成的 API
 * const result = await warehouseApi.getWarehouseList({ page: 1 })
 * ```
 * 
 * **App 端（uni-app X）**：
 * ```typescript
 * // 1. 创建适配器（让 uni.request 符合 IRequestClient 接口）
 * const uniAdapter: IRequestClient = {
 *   get: (url, config) => new Promise((resolve, reject) => {
 *     uni.request({
 *       url,
 *       method: 'GET',
 *       data: config?.params,
 *       success: (res) => resolve(res.data.data),
 *       fail: reject
 *     })
 *   }),
 *   // ... 其他方法
 * }
 * 
 * // 2. 调用工厂函数
 * import { createWarehouseApi } from '@bayes-mes/shared/api/warehouse/factory'
 * export const warehouseApi = createWarehouseApi(uniAdapter)
 * ```
 * 
 * ⚠️ 注意事项：
 * - **请求客户端接口**：所有工厂函数都使用 `api/core/client.ts` 中定义的 `IRequestClient` 接口
 * - **不要重复定义接口**：从 `../core/client` 导入，不要在每个模块中重新定义
 * - **适配器职责**：适配器负责将平台特定的请求客户端转换为符合 `IRequestClient` 的对象
 * - **响应数据解包**：适配器需要处理响应数据的解包（如 `res.data.data`）
 * - **类型安全**：所有方法都有完整的 TypeScript 类型定义
 * - **注释详细**：每个方法都有详细的中文注释，包括使用场景、参数、返回值、数据流向
 * 
 * 🔄 更新时机：
 * - 后端新增 API 方法
 * - 后端修改参数或响应结构
 * - 后端修改路由路径
 * 
 * ✅ 验证方式：
 * - 确保所有 Controller 的 public 方法都已映射
 * - 确保所有类型导入正确
 * - 确保 URL 路径与后端路由一致
 * - 运行 `npm run type-check` 验证 TypeScript 编译
 * 
 * @module api/warehouse/factory
 * @description Warehouse 模块 API 工厂函数
 * @generated AI-assisted
 * @author AI Assistant
 * @created 2025-10-18
 * @last-updated 2025-10-18
 * 
 * @change-log
 * - 2025-10-18: 初始创建，实现所有基础 CRUD 方法和特殊方法
 * ============================================================================
 */

// ===== 导入类型定义 =====
import type {
  Warehouse,                           // 主实体类型
  WarehouseLocation,                    // 库位实体类型
  WarehouseQueryParams,                // 列表查询参数（分页）
  WarehouseAllQueryParams,             // 所有仓库查询参数（不分页）
  LocationQueryParams,                  // 库位列表查询参数（分页）
  CreateWarehouseForm,                 // 创建表单
  UpdateWarehouseForm,                 // 更新表单
  CreateLocationForm,                  // 库位创建表单
  UpdateLocationForm,                  // 库位更新表单
  WarehouseStatistics,                 // 仓库统计数据
  WarehouseTypeStatistics,             // 类型统计数据
  WarehouseTypeStatisticsQueryParams,  // 类型统计查询参数
  BatchDeleteWarehousesForm,           // 批量删除表单
  UpdateWarehouseOrderForm,            // 更新排序表单
  BatchImportWarehousesForm,           // 批量导入表单
  WarehouseBatchImportResult,          // 批量导入结果
  WarehouseBatchDeleteResult,          // 批量删除结果
  BatchImportWarehouseLocationsForm,   // 库位批量导入表单
  WarehouseLocationBatchImportResult,  // 库位批量导入结果
} from '../../types/warehouse'

// ===== 导入通用类型 =====
import type { PaginationResult } from '../../types/api'

// ===== 导入统一的请求客户端接口 =====
// ⚠️ 重要：所有工厂函数都从这里导入接口，不要重复定义！
import type { IRequestClient } from '../core/client'

// ===== 导入 API 路由定义 =====
import { API_ROUTES } from '../routes'

// ===== 导出工厂函数 =====
/**
 * 创建 Warehouse 模块的 API 方法集合
 * 
 * 这是一个工厂函数，它接收一个"请求客户端"作为参数，
 * 返回一个包含所有 Warehouse 相关 API 方法的对象。
 * 
 * @param client - 请求客户端，必须实现 IRequestClient 接口
 *   - 可以是 Web 端的 axios 适配器
 *   - 可以是 App 端的 uni.request 适配器
 *   - 可以是测试用的 Mock 客户端
 * 
 * @returns API 方法集合对象，包含所有 Warehouse 相关的请求方法
 * 
 * 为什么这样设计？
 * - 解耦：API 逻辑与具体的请求实现分离
 * - 复用：同一套 API 定义可以在 Web 和 App 端使用
 * - 测试：可以注入 Mock 客户端进行单元测试
 */
export function createWarehouseApi(client: IRequestClient) {
  return {
    // ===== 基础 CRUD 操作 =====
    
    /**
     * 获取仓库列表（分页）
     * 
     * 使用场景：
     * - 在仓库管理页面的主列表中使用
     * - 支持分页、搜索、筛选功能
     * - 用户可以根据工厂、类型、状态等条件查询
     * 
     * @param params - 查询参数对象（可选）
     *   - factory_id?: number - 工厂ID筛选
     *     示例：factory_id: 1（只显示工厂1的仓库）
     *   - warehouse_type?: WarehouseType - 仓库类型筛选
     *     示例：warehouse_type: 'RAW'（只显示原料仓）
     *   - status?: WarehouseStatus - 状态筛选
     *     示例：status: 1（只显示启用的仓库）
     *   - search?: string - 搜索关键词，搜索编码或名称
     *     示例：search: '原料'（搜索包含"原料"的仓库）
     *   - per_page?: number - 每页数量，默认 20
     *     示例：per_page: 20（每页20条）
     * 
     * @returns Promise<PaginationResult<Warehouse>> 分页结果对象
     *   返回的数据结构：
     *   {
     *     data: Warehouse[],       // 仓库列表数组
     *     total: number,           // 总记录数
     *     current_page: number,    // 当前页码
     *     per_page: number,        // 每页数量
     *     last_page: number,       // 最后一页页码
     *     from: number,            // 当前页起始记录号
     *     to: number               // 当前页结束记录号
     *   }
     * 
     * 数据流向（完整过程）：
     * 1. 用户操作：用户在仓库管理页面点击"下一页"或输入搜索关键词
     * 2. 前端调用：warehouseApi.getWarehouseList({ page: 2, search: '原料' })
     * 3. 适配器处理：适配器将参数转换为 HTTP 请求
     * 4. 网络传输：发送到后端 GET /api/v1/warehouses?page=2&search=原料
     * 5. 后端处理：WarehouseController@index 方法
     *    - 验证查询参数
     *    - 应用筛选条件和分页
     *    - 通过 WarehouseResource 格式化数据
     * 6. 响应返回：后端返回 JSON 格式的分页数据
     * 7. 适配器解包：提取 response.data.data
     * 8. 前端接收：组件收到 PaginationResult<Warehouse> 类型的数据
     * 9. 页面更新：使用数据更新表格显示
     * 
     * 代码示例：
     * ```typescript
     * // 示例1：获取第一页
     * const page1 = await warehouseApi.getWarehouseList()
     * 
     * // 示例2：获取第二页，每页20条
     * const page2 = await warehouseApi.getWarehouseList({
     *   page: 2,
     *   per_page: 20
     * })
     * 
     * // 示例3：筛选原料仓
     * const rawWarehouses = await warehouseApi.getWarehouseList({
     *   warehouse_type: 'RAW'
     * })
     * 
     * // 示例4：组合查询
     * const result = await warehouseApi.getWarehouseList({
     *   factory_id: 1,
     *   warehouse_type: 'RAW',
     *   status: 1,
     *   search: '原料',
     *   per_page: 10
     * })
     * ```
     * 
     * 错误处理：
     * - 404：资源不存在
     * - 422：参数验证失败
     * - 500：服务器内部错误
     */
    getWarehouseList(params?: WarehouseQueryParams) {
      return client.get<PaginationResult<Warehouse>>(API_ROUTES.warehouse.list, { params })
    },
    
    /**
     * 获取所有仓库（不分页）
     * 
     * 使用场景：
     * - 在下拉选择框中使用
     * - 获取用于筛选的仓库列表
     * - 导出所有仓库数据
     * 
     * @param params - 查询参数（可选）
     *   - factory_id?: number - 工厂ID筛选
     *   - warehouse_type?: WarehouseType - 仓库类型筛选
     *   - only_active?: boolean - 只查询启用的仓库
     *     示例：only_active: true（只返回启用的仓库）
     * 
     * @returns Promise<Warehouse[]> 仓库数组
     * 
     * 数据流向：
     * 1. 前端调用：warehouseApi.getAllWarehouses({ only_active: true })
     * 2. 发送请求：GET /api/v1/warehouses/all?only_active=true
     * 3. 后端处理：WarehouseController@all 方法
     * 4. 返回数据：所有符合条件的仓库（不分页）
     * 
     * 代码示例：
     * ```typescript
     * // 获取所有启用的仓库
     * const activeWarehouses = await warehouseApi.getAllWarehouses({
     *   only_active: true
     * })
     * 
     * // 获取某工厂的所有原料仓
     * const rawWarehouses = await warehouseApi.getAllWarehouses({
     *   factory_id: 1,
     *   warehouse_type: 'RAW'
     * })
     * ```
     */
    getAllWarehouses(params?: WarehouseAllQueryParams) {
      return client.get<Warehouse[]>(API_ROUTES.warehouse.all, { params })
    },
    
    /**
     * 获取仓库详情
     * 
     * 使用场景：
     * - 在仓库详情页面显示完整信息
     * - 编辑仓库时获取当前数据
     * - 查看仓库的详细资料
     * 
     * @param id - 仓库的唯一标识符（主键）
     *   示例：id: 123（获取 ID 为 123 的仓库）
     * 
     * @returns Promise<Warehouse> 单个仓库对象
     *   返回的数据包含仓库的所有字段信息
     * 
     * 数据流向：
     * 1. 用户点击列表中的某一行
     * 2. 前端调用：warehouseApi.getWarehouseDetail(123)
     * 3. 发送请求：GET /api/v1/warehouses/123
     * 4. 后端处理：WarehouseController@show 方法
     * 5. 返回数据：单个 Warehouse 对象
     * 6. 前端显示：在详情页或编辑表单中展示数据
     * 
     * 代码示例：
     * ```typescript
     * // 获取指定仓库
     * const warehouse = await warehouseApi.getWarehouseDetail(123)
     * console.log(warehouse.warehouseName)
     * 
     * // 在详情页组件中使用
     * const { id } = useRoute().params
     * const warehouseData = await warehouseApi.getWarehouseDetail(Number(id))
     * ```
     * 
     * 错误处理：
     * - 404：仓库不存在
     * - 403：无权限访问该仓库
     */
    getWarehouseDetail(id: number) {
      return client.get<Warehouse>(API_ROUTES.warehouse.detail(id))
    },
    
    /**
     * 创建新仓库
     * 
     * 使用场景：
     * - 在仓库管理页面点击"新建"按钮
     * - 提交创建仓库的表单
     * 
     * @param data - 创建仓库的表单数据
     *   必填字段：
     *   - factory_id: number - 所属工厂ID
     *   - factory_code: string - 工厂编码
     *   - warehouse_code: string - 仓库编码（唯一）
     *   - warehouse_name: string - 仓库名称
     *   - warehouse_type: string - 仓库类型（RAW/SEMI/PRODUCT/LINE/REJECT）
     *   可选字段：
     *   - location: string - 位置描述
     *   - area: number - 面积
     *   - manager_id: number - 管理员ID
     *   - max_capacity: number - 最大容量
     *   - 等等...
     * 
     * @returns Promise<Warehouse> 创建成功后的仓库对象（包含生成的 ID）
     * 
     * 数据流向：
     * 1. 用户填写创建表单，点击"提交"
     * 2. 前端验证：表单验证
     * 3. 前端调用：warehouseApi.createWarehouse(formData)
     * 4. 发送请求：POST /api/v1/warehouses
     * 5. 后端处理：WarehouseController@store 方法
     *    - 验证数据
     *    - 检查编码唯一性
     *    - 插入数据库
     *    - 返回新创建的记录
     * 6. 前端处理：显示成功提示，跳转到列表或详情页
     * 
     * 代码示例：
     * ```typescript
     * const newWarehouse: CreateWarehouseForm = {
     *   factory_id: 1,
     *   factory_code: 'F001',
     *   warehouse_code: 'WH001',
     *   warehouse_name: '原料仓库A',
     *   warehouse_type: 'RAW',
     *   location: '厂区东侧',
     *   area: 1000,
     * }
     * 
     * const created = await warehouseApi.createWarehouse(newWarehouse)
     * message.success('创建成功')
     * router.push(`/warehouse/${created.id}`)
     * ```
     * 
     * 错误处理：
     * - 422：数据验证失败（必填字段缺失、编码重复等）
     * - 403：无权限创建
     */
    createWarehouse(data: CreateWarehouseForm) {
      return client.post<Warehouse>(API_ROUTES.warehouse.create, data)
    },
    
    /**
     * 更新仓库
     * 
     * 使用场景：
     * - 在仓库编辑页面修改后提交
     * - 更新仓库的部分字段
     * 
     * @param id - 要更新的仓库的 ID
     * @param data - 更新的数据（只需要包含要修改的字段）
     * 
     * @returns Promise<Warehouse> 更新后的完整仓库对象
     * 
     * 数据流向：
     * 1. 用户在编辑页面修改字段，点击"保存"
     * 2. 前端调用：warehouseApi.updateWarehouse(123, { warehouseName: '新名称' })
     * 3. 发送请求：PUT /api/v1/warehouses/123
     * 4. 后端处理：WarehouseController@update 方法
     * 5. 返回数据：更新后的完整数据
     * 
     * 代码示例：
     * ```typescript
     * // 更新仓库名称
     * const updated = await warehouseApi.updateWarehouse(123, {
     *   warehouse_name: '新名称'
     * })
     * 
     * // 更新多个字段
     * const updated2 = await warehouseApi.updateWarehouse(123, {
     *   warehouse_name: '新名称',
     *   location: '新位置',
     *   status: 1
     * })
     * ```
     * 
     * 错误处理：
     * - 404：仓库不存在
     * - 422：数据验证失败
     * - 403：无权限修改
     */
    updateWarehouse(id: number, data: UpdateWarehouseForm) {
      return client.put<Warehouse>(API_ROUTES.warehouse.warehouses(id), data)
    },
    
    /**
     * 删除仓库
     * 
     * 使用场景：
     * - 在仓库列表中点击"删除"按钮
     * - 在仓库详情页删除当前记录
     * 
     * @param id - 要删除的仓库的 ID
     * 
     * @returns Promise<void> 删除成功不返回数据
     * 
     * 数据流向：
     * 1. 用户点击删除按钮，弹出确认对话框
     * 2. 用户确认删除
     * 3. 前端调用：warehouseApi.deleteWarehouse(123)
     * 4. 发送请求：DELETE /api/v1/warehouses/123
     * 5. 后端处理：WarehouseController@destroy 方法
     *    - 检查是否可以删除（无库位等）
     *    - 软删除记录
     * 6. 前端处理：显示成功提示，刷新列表
     * 
     * 代码示例：
     * ```typescript
     * const handleDelete = async (id: number) => {
     *   const confirmed = await showConfirm('确定要删除吗？')
     *   if (!confirmed) return
     *   
     *   try {
     *     await warehouseApi.deleteWarehouse(id)
     *     message.success('删除成功')
     *     await loadList()
     *   } catch (error) {
     *     message.error('删除失败：' + error.message)
     *   }
     * }
     * ```
     * 
     * 错误处理：
     * - 404：仓库不存在
     * - 403：无权限删除
     * - 409：无法删除（存在库位等关联数据）
     */
    deleteWarehouse(id: number) {
      return client.delete<void>(API_ROUTES.warehouse.delete(id))
    },
    
    // ===== 批量操作 =====
    
    /**
     * 批量删除仓库
     * 
     * 使用场景：
     * - 在仓库列表中选中多个仓库后批量删除
     * 
     * @param data - 批量删除参数
     *   - ids: number[] - 要删除的仓库ID数组
     * 
     * @returns Promise<WarehouseBatchDeleteResult> 删除结果
     *   - deletedCount: number - 成功删除的数量
     * 
     * 数据流向：
     * 1. 用户在列表中选中多个仓库
     * 2. 点击"批量删除"按钮
     * 3. 前端调用：warehouseApi.batchDeleteWarehouses({ ids: [1, 2, 3] })
     * 4. 发送请求：POST /api/v1/warehouses/batch-delete
     * 5. 后端处理：逐个删除，返回成功数量
     * 
     * 代码示例：
     * ```typescript
     * const selectedIds = [1, 2, 3]
     * const result = await warehouseApi.batchDeleteWarehouses({
     *   ids: selectedIds
     * })
     * message.success(`成功删除 ${result.deletedCount} 个仓库`)
     * ```
     */
    batchDeleteWarehouses(data: BatchDeleteWarehousesForm) {
      return client.post<WarehouseBatchDeleteResult>(API_ROUTES.warehouse.batchDelete, data)
    },
    
    /**
     * 更新仓库排序
     * 
     * 使用场景：
     * - 在仓库列表中拖拽排序
     * - 批量设置仓库的显示顺序
     * 
     * @param data - 排序数据
     *   - orders: { id: number, sort_order: number }[] - 排序数组
     * 
     * @returns Promise<void> 更新成功不返回数据
     * 
     * 代码示例：
     * ```typescript
     * await warehouseApi.updateWarehouseOrder({
     *   orders: [
     *     { id: 1, sort_order: 10 },
     *     { id: 2, sort_order: 20 },
     *     { id: 3, sort_order: 30 }
     *   ]
     * })
     * ```
     */
    updateWarehouseOrder(data: UpdateWarehouseOrderForm) {
      return client.put<void>(API_ROUTES.warehouse.order, data)
    },
    
    // ===== 统计相关 =====
    
    /**
     * 获取仓库统计信息
     * 
     * 使用场景：
     * - 在仓库详情页显示统计卡片
     * - 显示库位统计、容量统计等
     * 
     * @param id - 仓库ID
     * 
     * @returns Promise<WarehouseStatistics> 统计信息
     *   包含：
     *   - warehouseId: number - 仓库ID
     *   - warehouseCode: string - 仓库编码
     *   - warehouseName: string - 仓库名称
     *   - locations: { total, active, inactive } - 库位统计
     *   - capacity: { total, used, available, usageRate, unit } - 容量统计
     * 
     * 数据流向：
     * 1. 用户打开仓库详情页
     * 2. 前端调用：warehouseApi.getWarehouseStatistics(123)
     * 3. 发送请求：GET /api/v1/warehouses/123/statistics
     * 4. 后端处理：WarehouseService@getStatistics 方法
     * 5. 返回数据：完整的统计信息
     * 
     * 代码示例：
     * ```typescript
     * const stats = await warehouseApi.getWarehouseStatistics(123)
     * console.log(`总库位：${stats.locations.total}`)
     * console.log(`容量使用率：${stats.capacity.usageRate}%`)
     * ```
     */
    getWarehouseStatistics(id: number) {
      return client.get<WarehouseStatistics>(API_ROUTES.warehouse.statistics(id))
    },
    
    /**
     * 获取仓库类型统计
     * 
     * 使用场景：
     * - 在仓库概览页显示类型分布图表
     * - 统计各类型仓库的数量
     * 
     * @param params - 查询参数（可选）
     *   - factory_id?: number - 按工厂筛选
     * 
     * @returns Promise<WarehouseTypeStatistics[]> 类型统计数组
     *   每个元素包含：
     *   - type: WarehouseType - 仓库类型
     *   - name: string - 类型名称
     *   - count: number - 该类型的仓库数量
     * 
     * 代码示例：
     * ```typescript
     * // 获取所有工厂的类型统计
     * const typeStats = await warehouseApi.getWarehouseTypeStatistics()
     * 
     * // 获取指定工厂的类型统计
     * const factoryStats = await warehouseApi.getWarehouseTypeStatistics({
     *   factory_id: 1
     * })
     * ```
     */
    getWarehouseTypeStatistics(params?: WarehouseTypeStatisticsQueryParams) {
      return client.get<WarehouseTypeStatistics[]>(API_ROUTES.warehouse.statisticsType, { params })
    },
    
    // ===== 特殊查询 =====
    
    /**
     * 根据编码查找仓库
     * 
     * 使用场景：
     * - 快速查找仓库
     * - 扫码入库时根据仓库编码查找
     * 
     * @param code - 仓库编码
     * 
     * @returns Promise<Warehouse> 仓库信息
     * 
     * 代码示例：
     * ```typescript
     * const warehouse = await warehouseApi.findWarehouseByCode('WH001')
     * ```
     */
    findWarehouseByCode(code: string) {
      return client.get<Warehouse>(API_ROUTES.warehouse.findByCode(code))
    },
    
    // ===== 导入导出 =====
    
    /**
     * 批量导入仓库
     * 
     * 使用场景：
     * - 批量导入仓库数据
     * - 初始化系统时导入仓库信息
     * 
     * @param data - 导入数据
     *   - warehouses: 仓库数据数组
     * 
     * @returns Promise<WarehouseBatchImportResult> 导入结果
     *   - totalCount: number - 总数量
     *   - successCount: number - 成功数量
     *   - failedCount: number - 失败数量
     *   - successItems: 成功的项
     *   - failedItems: 失败的项（含错误信息）
     * 
     * 代码示例：
     * ```typescript
     * const result = await warehouseApi.importWarehouses({
     *   warehouses: [
     *     {
     *       factory_id: 1,
     *       factory_code: 'F001',
     *       warehouse_code: 'WH001',
     *       warehouse_name: '原料仓库A',
     *       warehouse_type: 'RAW'
     *     },
     *     // ... 更多仓库
     *   ]
     * })
     * 
     * console.log(`成功导入 ${result.successCount} 个`)
     * console.log(`失败 ${result.failedCount} 个`)
     * ```
     */
    importWarehouses(data: BatchImportWarehousesForm) {
      return client.post<WarehouseBatchImportResult>(API_ROUTES.warehouse.import, data)
    },

    // ===== 库位相关 =====

    /**
     * 批量导入库位
     *
     * @description 从 Excel 解析出的库位数据批量导入。后端为逐实体事务部分成功语义：
     * 单行失败只记入 failedItems 不回滚好行；「已存在」行计入 failedItems 且消息含
     * "已存在，已跳过"（前端按跳过计数，断点续传/重传幂等安全）。
     *
     * ⚠️ 必须使用注入 batchHttpAdapter（120 秒超时）的 API 实例调用，
     * 不要用 15 秒默认客户端（后端长事务会前端先超时，重试造成并发导入堆积）。
     *
     * @param data - BatchImportWarehouseLocationsForm 单次最多 2000 行
     * @returns Promise<WarehouseLocationBatchImportResult> 导入结果
     *
     * 使用示例：
     * ```typescript
     * const result = await warehouseApi.importWarehouseLocations({
     *   locations: [
     *     {
     *       factory_id: 1,
     *       factory_code: 'F001',
     *       warehouse_id: 1,
     *       warehouse_code: 'WH01',
     *       location_code: 'A-01-01',
     *       location_name: 'A区1号架1层',
     *       location_type: 'SHELF'
     *     }
     *   ]
     * })
     * ```
     */
    importWarehouseLocations(data: BatchImportWarehouseLocationsForm) {
      return client.post<WarehouseLocationBatchImportResult>(API_ROUTES.warehouse.warehouseLocationsImport, data)
    },

    /**
     * 获取库位列表（分页）
     * 
     * 使用场景：
     * - 在库位管理页面显示列表
     * - 支持分页、搜索、筛选功能
     * - 用户可以根据仓库、区域、类型、状态等条件查询
     * 
     * @param params - 查询参数对象（可选）
     *   - keyword?: string - 关键词搜索（库位编码或名称，后端与 search 等价）
     *     示例：keyword: 'A-01'（搜索包含"A-01"的库位）
     *   - warehouse_id?: number - 仓库ID筛选
     *     示例：warehouse_id: 1（只显示仓库1的库位）
     *   - parent_id?: number - 父级库位ID筛选
     *     示例：parent_id: 10（只显示父级库位为10的子库位）
     *   - zone?: string - 区域筛选
     *     示例：zone: 'A区'（只显示A区的库位）
     *   - location_type?: string - 库位类型筛选
     *     示例：location_type: 'SHELF'（只显示货架类型）
     *   - status?: number - 状态筛选
     *     示例：status: 1（只显示启用的库位）
     *   - is_virtual?: boolean - 是否虚拟库位
     *     示例：is_virtual: false（只显示非虚拟库位）
     *   - allow_mix?: boolean - 是否允许混放
     *     示例：allow_mix: true（只显示允许混放的库位）
     *   - only_available?: boolean - 只查询可用库位
     *     示例：only_available: true（只返回可用的库位）
     *   - page?: number - 页码，默认 1
     *     示例：page: 2（获取第2页）
     *   - page_size?: number - 每页数量，默认 20
     *     示例：page_size: 50（每页50条）
     * 
     * @returns Promise<PaginationResult<WarehouseLocation>> 分页结果对象
     *   返回的数据结构：
     *   {
     *     items: WarehouseLocation[],    // 库位列表数组
     *     total: number,                 // 总记录数
     *     current_page: number,          // 当前页码
     *     per_page: number,              // 每页数量
     *     last_page: number,             // 最后一页页码
     *     from: number,                  // 当前页起始记录号
     *     to: number                     // 当前页结束记录号
     *   }
     * 
     * 数据流向（完整过程）：
     * 1. 用户操作：用户在库位管理页面点击"下一页"或输入搜索关键词
     * 2. 前端调用：warehouseApi.getLocationList({ page: 2, keyword: 'A-01' })
     * 3. 适配器处理：适配器将参数转换为 HTTP 请求
     * 4. 网络传输：发送到后端 GET /api/v1/warehouse-locations?page=2&keyword=A-01
     * 5. 后端处理：LocationController@index 方法
     *    - 验证查询参数
     *    - 应用筛选条件和分页
     *    - 通过 WarehouseLocationResource 格式化数据
     * 6. 响应返回：后端返回 JSON 格式的分页数据
     * 7. 适配器解包：提取 response.data.data
     * 8. 前端接收：组件收到 PaginationResult<WarehouseLocation> 类型的数据
     * 9. 页面更新：使用数据更新表格显示
     * 
     * 代码示例：
     * ```typescript
     * // 示例1：获取第一页
     * const page1 = await warehouseApi.getLocationList()
     * 
     * // 示例2：获取第二页，每页20条
     * const page2 = await warehouseApi.getLocationList({
     *   page: 2,
     *   page_size: 20
     * })
     * 
     * // 示例3：筛选仓库1的库位
     * const locations = await warehouseApi.getLocationList({
     *   warehouse_id: 1
     * })
     * 
     * // 示例4：组合查询
     * const result = await warehouseApi.getLocationList({
     *   warehouse_id: 1,
     *   zone: 'A区',
     *   location_type: 'SHELF',
     *   status: 1,
     *   keyword: 'A-01',
     *   page: 1,
     *   page_size: 20
     * })
     * ```
     * 
     * 错误处理：
     * - 404：资源不存在
     * - 422：参数验证失败
     * - 500：服务器内部错误
     */
    getLocationList(params?: LocationQueryParams) {
      return client.get<PaginationResult<WarehouseLocation>>(API_ROUTES.warehouse.warehouseLocationsList, { params })
    },

    /**
     * 获取所有库位（不分页）
     * 
     * 使用场景：
     * - 在下拉选择框中使用
     * - 获取用于筛选的库位列表
     * - 导出所有库位数据
     * 
     * @param params - 查询参数（可选）
     *   - warehouse_id?: number - 仓库ID筛选
     *   - status?: number - 状态筛选
     *   - only_available?: boolean - 只查询可用的库位
     * 
     * @returns Promise<WarehouseLocation[]> 库位数组
     * 
     * 数据流向：
     * 1. 前端调用：warehouseApi.getAllLocations({ warehouse_id: 1 })
     * 2. 发送请求：GET /api/v1/warehouse-locations/all?warehouse_id=1
     * 3. 后端处理：LocationController@all 方法
     * 4. 返回数据：所有符合条件的库位（不分页）
     * 
     * 代码示例：
     * ```typescript
     * // 获取仓库1的所有库位
     * const locations = await warehouseApi.getAllLocations({
     *   warehouse_id: 1
     * })
     * 
     * // 获取所有可用库位
     * const availableLocations = await warehouseApi.getAllLocations({
     *   only_available: true
     * })
     * ```
     */
    getAllLocations(params?: { warehouse_id?: number; status?: number; only_available?: boolean }) {
      return client.get<WarehouseLocation[]>(API_ROUTES.warehouse.warehouseLocationsAll, { params })
    },
    
    /**
     * 获取库位树形结构
     * 
     * 使用场景：
     * - 在库位管理页面显示树形结构
     * - 支持层级库位的展示
     * - 用于库位的父子关系展示
     * 
     * @param warehouseId - 仓库ID
     *   示例：warehouseId: 1（获取仓库1的所有库位树）
     * 
     * @param params - 查询参数（可选）
     *   - only_active?: boolean - 只查询启用的库位
     *     示例：only_active: true（只返回启用的库位）
     * 
     * @returns Promise<WarehouseLocation[]> 库位树形结构数组
     *   返回的数据结构：
     *   [
     *     {
     *       id: 1,
     *       warehouseId: 1,
     *       locationCode: 'A-01-01',
     *       locationName: 'A区1号架1层',
     *       // ... 其他字段
     *       children: [  // 子库位（如果有）
     *         {
     *           id: 2,
     *           // ...
     *           children: []
     *         }
     *       ]
     *     }
     *   ]
     * 
     * 数据流向：
     * 1. 用户打开库位管理页面，选择仓库
     * 2. 前端调用：warehouseApi.getLocationTree(1, { only_active: true })
     * 3. 发送请求：GET /api/v1/warehouse-locations/tree/1?only_active=true
     * 4. 后端处理：LocationController@tree 方法
     *    - 调用 LocationService@getTree
     *    - 构建树形结构（递归）
     *    - 通过 WarehouseLocationResource 格式化数据
     * 5. 返回数据：树形结构的库位数组
     * 6. 前端显示：在 Tree 组件中展示层级结构
     * 
     * 代码示例：
     * ```typescript
     * // 获取仓库1的所有库位树
     * const tree = await warehouseApi.getLocationTree(1)
     * 
     * // 只获取启用的库位
     * const activeTree = await warehouseApi.getLocationTree(1, {
     *   only_active: true
     * })
     * 
     * // 转换为 Tree 组件格式
     * const treeData = tree.map(loc => ({
     *   key: loc.id,
     *   title: loc.locationCode,
     *   location: loc,
     *   children: loc.children ? convertToTreeData(loc.children) : undefined
     * }))
     * ```
     * 
     * 错误处理：
     * - 404：仓库不存在
     * - 500：服务器内部错误
     */
    getLocationTree(warehouseId: number, params?: { only_active?: boolean; keyword?: string; zone?: string }) {
      return client.get<WarehouseLocation[]>(API_ROUTES.warehouse.tree(warehouseId), { params })
    },

    /**
     * 获取库位统计信息
     * 
     * @description 获取指定仓库的库位统计信息，包括总数、启用数、已满数、维护中数等
     * 
     * @param warehouseId 仓库ID
     * @returns 库位统计信息
     * 
     * 使用示例：
     * ```typescript
     * const stats = await warehouseApi.getLocationStatistics(1)
     * console.log(`总库位：${stats.total}`)
     * console.log(`启用：${stats.active}`)
     * console.log(`已满：${stats.full}`)
     * ```
     */
    getLocationStatistics(warehouseId: number) {
      return client.get<{ total: number; active: number; inactive: number; maintenance: number; full: number }>(
        API_ROUTES.warehouse.warehouseLocationsStatistics(warehouseId)
      )
    },

    /**
     * 创建库位
     * 
     * @description 在指定仓库中创建新库位
     * 
     * @param data 创建库位的表单数据
     * @returns 创建成功后的库位对象
     * 
     * 使用示例：
     * ```typescript
     * const newLocation = await warehouseApi.createLocation({
     *   warehouse_id: 1,
     *   warehouse_code: 'WH001',
     *   location_code: 'A-01-01',
     *   location_name: 'A区1号架1层',
     *   location_type: 'SHELF'
     * })
     * ```
     */
    createLocation(data: CreateLocationForm) {
      return client.post<WarehouseLocation>(API_ROUTES.warehouse.warehouseLocationsList, data)
    },

    /**
     * 更新库位
     * 
     * @description 更新指定库位的信息
     * 
     * @param id 库位ID
     * @param data 更新的数据（只需包含要修改的字段）
     * @returns 更新后的完整库位对象
     * 
     * 使用示例：
     * ```typescript
     * const updated = await warehouseApi.updateLocation(123, {
     *   location_name: '新名称',
     *   status: 1
     * })
     * ```
     */
    updateLocation(id: number, data: UpdateLocationForm) {
      return client.put<WarehouseLocation>(API_ROUTES.warehouse.warehouseLocations(id), data)
    },

    /**
     * 删除库位
     * 
     * @description 删除指定库位
     * 
     * @param id 库位ID
     * @returns void
     * 
     * 使用示例：
     * ```typescript
     * await warehouseApi.deleteLocation(123)
     * message.success('删除成功')
     * ```
     */
    deleteLocation(id: number) {
      return client.delete<void>(API_ROUTES.warehouse.warehouseLocations(id))
    },

    /**
     * 获取库位详情
     * 
     * @description 获取指定库位的详细信息
     * 
     * @param id 库位ID
     * @returns 库位详情对象
     */
    getLocationDetail(id: number) {
      return client.get<WarehouseLocation>(API_ROUTES.warehouse.warehouseLocations(id))
    },
  }
}

